/*
  Blair TLV product lightbox (Claude 2026-07-09).
  <product-modal> extends Dawn's ModalDialog (focus trap, body-scroll lock,
  Escape-to-close, pauseAllMedia) and adds an Apple-Music-style ambient preview:
    - a horizontal transform track of 1:1 square slides
    - swipe (touch) / drag (mouse) / arrow keys move between items
    - pinch (2-finger), ctrl+wheel, or double-tap zooms the active media;
      while zoomed, dragging pans and slide-swiping is disabled
    - the active slide's video autoplays (muted/loop/playsinline); others pause
  No external libraries: plain Pointer Events + a hand-rolled gesture handler.
*/

if (!customElements.get('product-modal')) {
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const SWIPE_RATIO = 0.16; // fraction of slide width needed to change slide
  const DOUBLE_TAP_MS = 300;

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.index = 0;
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.pointers = new Map();
        this.dragMode = null; // 'swipe' | 'pan' | 'pinch' | null
        this._lastTap = 0;
        this._bound = false;
      }

      _cache() {
        if (this._cached) return;
        this.track = this.querySelector('[data-bt-track]');
        this.slides = Array.from(this.querySelectorAll('.bt-lightbox__slide'));
        this.counter = this.querySelector('[data-bt-counter]');
        this.prevBtn = this.querySelector('[data-bt-prev]');
        this.nextBtn = this.querySelector('[data-bt-next]');
        this._cached = !!this.track;
      }

      _bindOnce() {
        if (this._bound || !this.track) return;
        this._bound = true;

        const t = this.track;
        t.addEventListener('pointerdown', this._onPointerDown.bind(this));
        t.addEventListener('pointermove', this._onPointerMove.bind(this));
        t.addEventListener('pointerup', this._onPointerUp.bind(this));
        t.addEventListener('pointercancel', this._onPointerUp.bind(this));
        // ctrl+wheel to zoom on desktop (never let the page scroll under us)
        t.addEventListener('wheel', this._onWheel.bind(this), { passive: false });

        this.addEventListener('keydown', this._onKeyDown.bind(this));

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => this.go(this.rtl() ? this.index + 1 : this.index - 1));
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => this.go(this.rtl() ? this.index - 1 : this.index + 1));
        }
      }

      rtl() {
        return (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
      }

      /* ---- open / close ---------------------------------------------------- */

      show(opener) {
        this._cache();
        this._bindOnce();
        super.show(opener); // focus trap + body lock + pauseAllMedia

        let target = 0;
        const id = opener && opener.getAttribute && opener.getAttribute('data-media-id');
        if (id) {
          const i = this.slides.findIndex((s) => s.getAttribute('data-media-id') === id);
          if (i >= 0) target = i;
        }
        this.index = target;
        this._resetZoom(false);
        this._layout(false);
        this._activate();
      }

      hide() {
        this._resetZoom(false);
        if (this.slides) {
          this.slides.forEach((s) => {
            const v = s.querySelector('video');
            if (v) v.pause();
          });
        }
        super.hide(); // removes body lock, pauseAllMedia, releases focus trap
      }

      /* ---- slide navigation ------------------------------------------------ */

      go(i) {
        if (this.scale > 1.02) return; // zoomed: swiping disabled
        const n = this.slides.length;
        if (!n) return;
        this.index = clamp(i, 0, n - 1);
        this._layout(!this.reducedMotion);
        this._activate();
      }

      _layout(animate) {
        if (!this.track) return;
        this.track.classList.toggle('is-dragging', !animate);
        // track is forced dir:ltr, so a positive index always shifts left
        const offset = -this.index * this.track.clientWidth;
        this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
        if (this.counter) {
          this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
        }
        if (this.prevBtn) this.prevBtn.toggleAttribute('hidden', this.index === 0);
        if (this.nextBtn) this.nextBtn.toggleAttribute('hidden', this.index === this.slides.length - 1);
      }

      _activate() {
        this.slides.forEach((slide, i) => {
          const active = i === this.index;
          slide.classList.toggle('is-active', active);
          const v = slide.querySelector('video');
          if (!v) return;
          if (active) {
            v.muted = true;
            const p = v.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          } else {
            v.pause();
          }
        });
      }

      /* ---- zoom + pan ------------------------------------------------------ */

      _activeZoomEl() {
        const slide = this.slides[this.index];
        return slide ? slide.querySelector('[data-bt-zoom]') : null;
      }

      _applyZoom(animate) {
        const el = this._activeZoomEl();
        if (!el) return;
        el.classList.toggle('is-animating', !!animate && !this.reducedMotion);
        el.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
        if (this.track) this.track.classList.toggle('is-zoomed', this.scale > 1.02);
      }

      _clampPan() {
        const el = this._activeZoomEl();
        if (!el) return;
        const rect = el.getBoundingClientRect();
        // rect is already scaled; max travel keeps the scaled media covering the stage
        const maxX = Math.max(0, (rect.width - rect.width / this.scale) / 2);
        const maxY = Math.max(0, (rect.height - rect.height / this.scale) / 2);
        this.tx = clamp(this.tx, -maxX, maxX);
        this.ty = clamp(this.ty, -maxY, maxY);
      }

      _resetZoom(animate) {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this._applyZoom(animate);
      }

      _zoomTo(scale, animate) {
        this.scale = clamp(scale, MIN_SCALE, MAX_SCALE);
        if (this.scale <= 1.02) {
          this.tx = 0;
          this.ty = 0;
        } else {
          this._clampPan();
        }
        this._applyZoom(animate);
      }

      /* ---- pointer gestures ------------------------------------------------ */

      _onPointerDown(e) {
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        try {
          this.track.setPointerCapture(e.pointerId);
        } catch (err) {
          /* ignore */
        }

        if (this.pointers.size === 2) {
          // begin pinch
          const pts = Array.from(this.pointers.values());
          this.dragMode = 'pinch';
          this._pinchStartDist = dist(pts[0], pts[1]) || 1;
          this._pinchStartScale = this.scale;
          this._pinchStartMid = mid(pts[0], pts[1]);
          this._panStartTx = this.tx;
          this._panStartTy = this.ty;
          return;
        }

        // single pointer
        this._startX = e.clientX;
        this._startY = e.clientY;
        this._trackBase = -this.index * this.track.clientWidth;
        this._panStartTx = this.tx;
        this._panStartTy = this.ty;
        this._moved = false;
        this.dragMode = this.scale > 1.02 ? 'pan' : 'swipe';
        if (this.dragMode === 'swipe') this.track.classList.add('is-dragging');
      }

      _onPointerMove(e) {
        if (!this.pointers.has(e.pointerId)) return;
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (this.dragMode === 'pinch' && this.pointers.size >= 2) {
          e.preventDefault();
          const pts = Array.from(this.pointers.values());
          const d = dist(pts[0], pts[1]) || 1;
          const m = mid(pts[0], pts[1]);
          this.scale = clamp((this._pinchStartScale * d) / this._pinchStartDist, MIN_SCALE, MAX_SCALE);
          // follow the fingers so the pinch feels anchored
          this.tx = this._panStartTx + (m.x - this._pinchStartMid.x);
          this.ty = this._panStartTy + (m.y - this._pinchStartMid.y);
          this._clampPan();
          this._applyZoom(false);
          return;
        }

        const dx = e.clientX - this._startX;
        const dy = e.clientY - this._startY;
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this._moved = true;

        if (this.dragMode === 'pan') {
          e.preventDefault();
          this.tx = this._panStartTx + dx;
          this.ty = this._panStartTy + dy;
          this._clampPan();
          this._applyZoom(false);
        } else if (this.dragMode === 'swipe') {
          e.preventDefault();
          this.track.style.transform = `translate3d(${this._trackBase + dx}px, 0, 0)`;
        }
      }

      _onPointerUp(e) {
        const wasPinch = this.dragMode === 'pinch';
        this.pointers.delete(e.pointerId);
        try {
          this.track.releasePointerCapture(e.pointerId);
        } catch (err) {
          /* ignore */
        }

        if (wasPinch) {
          if (this.pointers.size < 2) {
            this.dragMode = null;
            if (this.scale <= 1.02) this._resetZoom(true);
            else this._applyZoom(false);
          }
          return;
        }

        if (this.dragMode === 'swipe') {
          const dx = e.clientX - this._startX;
          const threshold = this.track.clientWidth * SWIPE_RATIO;
          this.track.classList.remove('is-dragging');
          if (dx <= -threshold) this.go(this.index + 1);
          else if (dx >= threshold) this.go(this.index - 1);
          else this._layout(!this.reducedMotion);
        } else if (this.dragMode === 'pan') {
          this._clampPan();
          this._applyZoom(false);
        }

        // double-tap / double-click to toggle zoom (only a clean tap, not a drag)
        if (!this._moved && this.pointers.size === 0) {
          const now = Date.now();
          if (now - this._lastTap < DOUBLE_TAP_MS) {
            this._lastTap = 0;
            if (this.scale > 1.02) this._zoomTo(1, true);
            else this._zoomTo(2.5, true);
          } else {
            this._lastTap = now;
            // a clean tap on the dark backdrop (not on the media) closes
            if (e.target && e.target.classList && e.target.classList.contains('bt-lightbox__bg')) {
              this.hide();
            }
          }
        }

        this.dragMode = null;
      }

      _onWheel(e) {
        if (!e.ctrlKey) return; // trackpad pinch / ctrl+wheel only
        e.preventDefault();
        const factor = Math.exp(-e.deltaY * 0.0016);
        this._zoomTo(this.scale * factor, false);
      }

      _onKeyDown(e) {
        const key = e.key;
        if (key === 'ArrowRight') {
          e.preventDefault();
          this.go(this.rtl() ? this.index - 1 : this.index + 1);
        } else if (key === 'ArrowLeft') {
          e.preventDefault();
          this.go(this.rtl() ? this.index + 1 : this.index - 1);
        }
      }
    }
  );

  /*
    Open the lightbox when a gallery VIDEO is tapped. The autoplaying inline
    video sits above Dawn's modal-opener toggle (z-index), so a tap lands on the
    <video>, not the hidden toggle button. This delegate finds that container's
    opener and shows the same modal at the right slide. Image thumbnails keep
    using Dawn's own modal-opener toggle.
  */
  document.addEventListener(
    'click',
    (e) => {
      const container =
        e.target &&
        e.target.closest &&
        e.target.closest('.product__media-list .product-media-container.media-type-video');
      if (!container) return;
      const opener = container.querySelector('modal-opener[data-modal]');
      const toggle = container.querySelector('.product__media-toggle');
      if (!opener || !toggle) return;
      const modal = document.querySelector(opener.getAttribute('data-modal'));
      if (modal && typeof modal.show === 'function') {
        e.preventDefault();
        modal.show(toggle);
      }
    },
    true
  );
}
